import { yupResolver } from '@hookform/resolvers/yup';
import { DialogContent, DialogTitle, Grid, Stack, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import {
  addingContactLinkInCandidateMaster,
  // removeContactLinkInCandidateMaster,
  updateContactLinkInCandidateMaster,
  ICandidate,
  getCandidateDetailsForGDPR,
  mergeCandidates,
  ICandidatesMergeDTO,
} from 'wisestep-api';
import {
  ControlledTextInput,
  Item,
  WsButton,
  WsDialog,
  WsTypoColors,
  WsColors,
  WsListingLoader,
} from 'wisestep-base';
import { object, string, array } from 'yup';

import { warning, success } from '../../../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../../../utils/NotificationStore';

// import WsCheckBox from '../../../components/WsCheckBox/WsCheckBox';
import MergeProfiles from './MergeProfiles';
interface IAddEmail {
  id?: string;
  open: boolean;
  onClose?: () => void;
  isEmail: boolean;
  isPhone: boolean;
  isMerge?: boolean;
  existingCandidate?: ICandidate;
  newEmail?: string;
  candidate: ICandidate;
}

const AddContactLink: FC<IAddEmail> = ({
  id,
  open,
  onClose,
  isEmail,
  isPhone,
  isMerge = false,
  existingCandidate,
  newEmail,
  candidate,
}) => {
  const [action, setAction] = useState<string>('');
  const [hasMerge, setHasMerge] = useState(false);
  const [disableMerge, setDisableMerge] = useState(false);
  const [processMerge, setProcessMerge] = useState(false);
  const [mergeCandidate, setMergeCandidate] = useState<ICandidate>();
  const formOptions = {
    resolver: yupResolver(
      object().shape({
        newData: string().when('linkedIn', {
          is: isEmail || isPhone,
          then: string().required('Empty field detected'),
        }),
        email: array().when('processMerge', {
          is: true,
          then: array()
            .min(1, 'at least one email should be selected')
            .max(3, 'max 3 emails can be selected'),
        }),
        phone: array().when('processMerge', {
          is: true,
          then: array().max(3, 'max 3 phone numbers can be selected'),
        }),
        profile: array().when('processMerge', {
          is: true,
          then: array().max(1, 'max 1 Linkedin url can  be selected'),
        }),
      })
    ),
    defaultValues: {
      linkedIn: candidate?.socialUrls?.[0],
      processMerge: false,
    },
  };
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [showCheckField, setShowCheckField] = useState(false);
  const methods = useForm(formOptions);
  const {
    handleSubmit,
    setValue,
    formState: { dirtyFields },
  } = methods;
  const [textLabel, setTextLabel] = useState<string>('');

  const setActionAndLabel = (action: string, label: string) => {
    setAction(action);
    setTextLabel(label);
  };

  const onCloseAction = () => {
    setProcessMerge(false);
    setHasMerge(false);
    setMergeCandidate(undefined);
    setValue('processMerge', false);
    methods.reset({
      linkedIn: candidate?.socialUrls?.[0],
      processMerge: false,
    });
    methods.clearErrors();
    onClose && onClose();
  };
  useEffect(() => {
    if (isMerge && existingCandidate && newEmail) {
      setHasMerge(true);
      setMergeCandidate(existingCandidate);
      setValue('newData', newEmail);
    }
  }, [isMerge, existingCandidate, newEmail, setValue]);

  useEffect(() => {
    if (isPhone) {
      setActionAndLabel('Phone', 'Enter new phone number');
    } else if (isEmail) {
      setShowCheckField(true);
      setActionAndLabel('Email', 'Enter new email');
    } else {
      setActionAndLabel('Profile URL', 'Enter new social link');
    }
    // case 'updateName':
    //   setOperation('add');
    //   setValue('newData', actionAndValue.value);
    //   setActionAndLabel('Name', 'Update Name');
  }, [isEmail, isPhone]);

  const refreshPage = () => {
    onClose && onClose();
    window.location.reload();
  };

  const addNewField = async (values: FieldValues, isPrimaryEmail?: boolean) => {
    const data = values.newData ?? values.linkedIn;
    if ((action === 'Email' || action === 'Profile URL') && !hasMerge && !processMerge) {
      try {
        const response = await getCandidateDetailsForGDPR(data, candidate?.workspaceId ?? '');
        const existingCandidate = response?.data;
        if (
          existingCandidate &&
          candidate?.candidateId !== existingCandidate?.candidateId &&
          !processMerge
        ) {
          setHasMerge(true);
          setMergeCandidate(existingCandidate);
          return;
        }
      } catch (e) {
        console.info('No exiting Candidate linked');
      }
    }
    let linksToRemove: string[] = [];
    if (hasMerge && processMerge) {
      const selectedLinks = [
        ...(values?.email ?? []),
        ...(values?.phone ?? []),
        ...(values?.profile ?? []),
      ];
      const allLinks = [
        candidate?.email ?? '',
        ...(candidate?.alternateEmails ?? []),
        candidate?.phoneNumber ?? '',
        ...(candidate?.alternatePhoneNumbers ?? []),
        mergeCandidate?.email ?? '',
        ...(mergeCandidate?.alternateEmails ?? []),
        mergeCandidate?.phoneNumber ?? '',
        ...(mergeCandidate?.alternatePhoneNumbers ?? []),
        candidate?.profileURLs?.[0] ?? '',
        mergeCandidate?.profileURLs?.[0] ?? '',
        candidate?.socialUrls?.[0] ?? '',
        mergeCandidate?.socialUrls?.[0] ?? '',
      ];
      linksToRemove = allLinks.filter(
        (link) => selectedLinks.findIndex((selectedLink) => selectedLink === link) === -1
      );
    }

    try {
      if (hasMerge && processMerge) {
        const res = await mergeCandidates({
          newCandidateId: candidate?.candidateId ?? candidate?.id ?? '',
          mergedCandidateId: mergeCandidate?.candidateId ?? '',
          emails: values?.email ?? [],
          newLink: values?.profile ? values?.profile[0] : '',
          phoneNumbers: values?.phone ?? [],
        } as ICandidatesMergeDTO);
        if (res?.data) {
          setNotification(success('Candidates Merged Successfully'));
          refreshPage();
        } else {
          setNotification(warning('Merging Candidates Failed'));
        }
      } else {
        await addingContactLinkInCandidateMaster({
          id: id ?? '',
          newEmail: action === 'Email' ? data : null,
          newLink: action === 'Profile URL' ? data : null,
          newPhoneNumber: action === 'Phone' ? data : null,
          newTag: action === 'Tag' ? data : null,
          updatePrimaryEmail: isPrimaryEmail ?? false,
          linksToRemove: linksToRemove,
        });

        setNotification(success('New field added'));
        refreshPage();
      }
    } catch (error) {
      console.log(error);
      setNotification(warning('Something went wrong'));
    }
  };

  const updateName = async (data: string) => {
    await updateContactLinkInCandidateMaster({
      id: id ?? '',
      newName: data,
    });
    setNotification(success('Name Updated'));
    refreshPage();
  };

  const onFormSubmit = async (data: FieldValues) => {
    if (hasMerge && processMerge) setDisableMerge(true);
    switch (action) {
      case 'Email':
        if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(data.newData)) {
          await setTimeout(() => setValue('newData', ''), 200);
          setNotification(warning('Please enter valid email'));
          return null;
        }
        addNewField(data, data.isPrimaryEmail);
        break;
      case 'Profile URL':
        if (
          !new RegExp(
            '((http|https)://)?(www.)?[a-zA-Z0-9@:%._\\+~#?&//=]{2,256}\\.[a-z]{2,6}\\b([-a-zA-Z0-9@:%._\\+~#?&//=]*)'
          ).test(data.linkedIn ?? data.newData)
        ) {
          await setTimeout(() => setValue('linkedIn', ''), 200);
          setNotification(warning('Please enter valid url'));
          return null;
        }
        addNewField(data);
        break;
      case 'Phone':
        if (
          !/^$|^[+(]?\(?\(?\d{1,4}?[-.()\s]?\(?\d{1,3}?\)?[-.()\s]?\d{1,4}[-.()\s]?\d{1,4}[-.()\s]?\d{1,9}[)\d]$/.test(
            data.newData
          )
        ) {
          await setTimeout(() => setValue('newData', ''), 200);
          setNotification(warning('Please enter valid phone number'));
          return null;
        }
        addNewField(data);
        break;
      case 'Tag':
        addNewField(data);
        break;
      case 'Name':
        updateName(data.newData);
        break;
    }
  };

  return (
    <FormProvider {...methods}>
      <form>
        <Grid container columnSpacing={2}>
          <Grid item xs={8}>
            <ControlledTextInput
              name={'linkedIn'}
              type={'text'}
              labelText={'LinkedIn URL'}
              variant="outlined"
              fullWidth
              required={false}
              defaultValue={candidate?.linkedInURL ?? candidate?.profileURLs?.[0] ?? ''}
            />
          </Grid>
          <Grid item xs={4} mt={4.5}>
            {dirtyFields.linkedIn && (
              <WsButton type="button" onClick={handleSubmit(onFormSubmit)}>
                Save
              </WsButton>
            )}
          </Grid>
        </Grid>
        <WsDialog openDialog={open || hasMerge} size={processMerge ? 'medium' : 'small'}>
          <DialogTitle>
            {hasMerge ? 'Merge Duplicate Profiles' : isEmail ? 'Add Email' : 'Add Phone No'}
          </DialogTitle>
          <DialogContent
            sx={{
              bgcolor: processMerge ? WsColors.BorderGrey : WsColors.White,
              px: 0,
              py: hasMerge ? 0 : 0.5,
            }}
          >
            <Grid
              container
              sx={{ bgcolor: hasMerge ? WsColors.BorderGrey : WsColors.White }}
              mt={hasMerge ? 0 : 1}
              borderRadius={1}
            >
              <Grid
                item
                xs={12}
                p={3}
                bgcolor={processMerge ? WsColors.BorderGrey : WsColors.White}
              >
                {hasMerge ? (
                  <>
                    {disableMerge ? (
                      <WsListingLoader />
                    ) : processMerge ? (
                      <MergeProfiles currentCandidate={candidate} mergeCandidate={mergeCandidate} />
                    ) : (
                      <Typography
                        color={WsTypoColors.BLACK}
                        variant="h5"
                        dangerouslySetInnerHTML={{
                          __html: 'Review profiles with common email/Linkedin URL',
                        }}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <ControlledTextInput
                      name="newData"
                      labelText={action}
                      type="text"
                      fullWidth
                      id="newData"
                      placeholder={textLabel}
                      variant="outlined"
                      autoComplete="marginTop"
                    />
                    {false && (
                      <>
                        {showCheckField}
                        {/* <WsCheckBox name="isPrimaryEmail" label="Mark Email as Primary" /> */}
                      </>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <Grid container item xs={12} justifyContent="flex-end">
            <Item>
              <Stack direction="row" spacing={2}>
                {(!hasMerge || processMerge) && (
                  <WsButton
                    type="button"
                    onClick={handleSubmit(onFormSubmit)}
                    disabled={disableMerge}
                  >
                    {processMerge ? 'Merge' : 'Save'}
                  </WsButton>
                )}
                {hasMerge && !processMerge && (
                  <WsButton
                    type="button"
                    onClick={() => {
                      setValue('processMerge', true);
                      setProcessMerge(true);
                    }}
                  >
                    Merge
                  </WsButton>
                )}
                <WsButton variant="outlined" onClick={onCloseAction}>
                  Close
                </WsButton>
              </Stack>
            </Item>
          </Grid>
        </WsDialog>
      </form>
    </FormProvider>
  );
};

export default AddContactLink;
